/**
 * useAudioRecorder Hook
 * Custom hook for recording audio and converting it to base64
 */

import { useState, useRef, useCallback } from 'react';

export interface UseAudioRecorderResult {
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | null>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  cancelRecording: () => void;
  error: string | null;
  clearError: () => void; // Clear error state (e.g. when user dismisses the message)
  isActuallyRecording: () => boolean; // Check actual MediaRecorder state
  cleanup: () => void; // Cleanup function for component unmount
}

export function useAudioRecorder(): UseAudioRecorderResult {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      audioChunksRef.current = [];
      setRecordingTime(0);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('MediaDevices API not supported in this browser');
      }

      // Check if we already have an active stream
      if (streamRef.current && streamRef.current.active) {
        // Reuse existing stream if it's still active
        const existingStream = streamRef.current;
        const audioTracks = existingStream.getAudioTracks();

        if (audioTracks.length > 0 && audioTracks[0].readyState === 'live') {
          // Stream is still active, reuse it
          // Just create a new MediaRecorder with the existing stream
          const mimeTypes = [
            'audio/webm',
            'audio/webm;codecs=opus',
            'audio/ogg',
            'audio/mp4',
            'audio/mpeg',
            'audio/wav',
          ];

          let selectedMimeType = '';
          for (const mimeType of mimeTypes) {
            if (MediaRecorder.isTypeSupported(mimeType)) {
              selectedMimeType = mimeType;
              break;
            }
          }

          const mediaRecorder = new MediaRecorder(existingStream, {
            mimeType: selectedMimeType || undefined,
          });

          mediaRecorderRef.current = mediaRecorder;

          mediaRecorder.ondataavailable = event => {
            if (event.data.size > 0) {
              audioChunksRef.current.push(event.data);
            }
          };

          mediaRecorder.onerror = () => {
            setError('Recording error occurred');
          };

          mediaRecorder.start(100);

          setIsRecording(true);
          setIsPaused(false);

          timerRef.current = setInterval(() => {
            setRecordingTime(prev => prev + 1);
          }, 1000);

          return; // Exit early, we're using existing stream
        }
      }

      // Check permission state first (if supported)
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: 'microphone' as PermissionName,
          });

          // If permission is denied, throw error immediately
          if (permissionStatus.state === 'denied') {
            throw new Error(
              'Microphone permission is denied. Please enable it in your browser settings.'
            );
          }
        } catch (permError) {
          // Permissions API might not be fully supported, continue anyway
          console.warn('Permissions API not fully supported:', permError);
        }
      }

      // Request new stream (browser won't show prompt if permission already granted)
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const audioTracks = stream.getAudioTracks();

      if (audioTracks.length === 0) {
        throw new Error('No audio tracks found in stream');
      }

      streamRef.current = stream;

      const mimeTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/ogg',
        'audio/mp4',
        'audio/mpeg',
        'audio/wav',
      ];

      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType || undefined,
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = () => {
        setError('Recording error occurred');
      };

      mediaRecorder.start(100);

      setIsRecording(true);
      setIsPaused(false);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to start recording. Please check microphone permissions.';
      setError(errorMessage);
      setIsRecording(false);
    }
  }, []);

  /**
   * Stop recording and convert to base64
   */
  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise(resolve => {
      if (!mediaRecorderRef.current) {
        resolve(null);
        return;
      }

      const mediaRecorder = mediaRecorderRef.current;

      if (mediaRecorder.state === 'inactive') {
        resolve(null);
        return;
      }

      mediaRecorder.onstop = async () => {
        try {
          // Don't stop the stream tracks here - keep them alive for reuse
          // This prevents the browser from asking for permission again
          // The stream will be cleaned up when cancelRecording is called or component unmounts

          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }

          if (audioChunksRef.current.length === 0) {
            setError('No audio data was recorded. Please try again.');
            setIsRecording(false);
            setIsPaused(false);
            setRecordingTime(0);
            resolve(null);
            return;
          }

          const blobType = audioChunksRef.current[0]?.type || 'audio/webm';
          const audioBlob = new Blob(audioChunksRef.current, {
            type: blobType,
          });

          if (audioBlob.size === 0) {
            setError('Recorded audio is empty. Please check your microphone.');
            setIsRecording(false);
            setIsPaused(false);
            setRecordingTime(0);
            audioChunksRef.current = [];
            resolve(null);
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            const base64Data = base64String.split(',')[1] || base64String;

            if (!base64Data || base64Data.length === 0) {
              setError('Failed to convert audio to base64');
              resolve(null);
              return;
            }

            resolve(base64Data);
          };
          reader.onerror = () => {
            setError('Failed to convert audio to base64');
            resolve(null);
          };
          reader.readAsDataURL(audioBlob);
        } catch {
          setError('Failed to process recorded audio');
          resolve(null);
        } finally {
          setIsRecording(false);
          setIsPaused(false);
          setRecordingTime(0);
          audioChunksRef.current = [];
        }
      };

      if (
        mediaRecorder.state === 'recording' ||
        mediaRecorder.state === 'paused'
      ) {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.requestData();
        }

        setTimeout(() => {
          if (
            mediaRecorder.state === 'recording' ||
            mediaRecorder.state === 'paused'
          ) {
            mediaRecorder.stop();
          } else {
            if (mediaRecorder.onstop) {
              mediaRecorder.onstop(new Event('stop'));
            }
          }
        }, 100);
      } else {
        if (mediaRecorder.onstop) {
          mediaRecorder.onstop(new Event('stop'));
        }
      }
    });
  }, []);

  /**
   * Pause recording
   */
  const pauseRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, []);

  /**
   * Resume recording
   */
  const resumeRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'paused'
    ) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  }, []);

  /**
   * Cancel recording without saving
   */
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }

    // Stop stream tracks when explicitly cancelling
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsRecording(false);
    setIsPaused(false);
    setRecordingTime(0);
    audioChunksRef.current = [];
    setError(null);
  }, []);

  /**
   * Cleanup function to stop all tracks when component unmounts
   * This should be called from useEffect cleanup in components using this hook
   */
  const cleanup = useCallback(() => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Clear error state (e.g. when user dismisses the error message)
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check if actually recording (not just React state)
   * This is useful when Fast Refresh resets component state
   */
  const isActuallyRecording = useCallback((): boolean => {
    const state = mediaRecorderRef.current?.state;
    return state === 'recording' || state === 'paused';
  }, []);

  return {
    isRecording,
    isPaused,
    recordingTime,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    error,
    clearError,
    isActuallyRecording,
    cleanup, // Export cleanup for component unmount
  };
}
